import React, { useState, useMemo, useEffect } from 'react';
import { FaRegListAlt, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

/**
 * Composant TopRankingCard - Affiche un classement dynamique Top X
 *
 * @param {string} title - Titre du classement
 * @param {Array} data - Données du classement [{ id, name, value, unit?, icon? }]
 * @param {number} topX - Nombre d'éléments à afficher par page (défaut: 5)
 * @param {string} valueLabel - Libellé de la valeur (défaut: 'Score')
 * @param {function} onItemClick - Callback au clic sur un élément
 * @param {string} colorTheme - Theme de couleur (blue/green/yellow/red/purple/orange/pink)
 * @param {boolean} compact - Mode compact pour affichage en grille
 * @param {string} emptyMessage - Message quand aucune donnée
 * @param {boolean} showPagination - Afficher la pagination si plus de topX éléments
 * @param {boolean} showTopXSelector - Afficher le selecteur Top X
 * @param {array} topXOptions - Options pour le selecteur Top X (défaut: [5, 10, 15, 20])
 * @param {number|null} fixedPageSize - Taille fixe de page (ex: 5) pour paginer un Top X
 * @param {React.ReactNode} headerControls - Contrôles additionnels affichés à droite du header
 */
const TopRankingCard = ({
  title,
  data = [],
  topX = 5,
  valueLabel = 'Score',
  onItemClick,
  colorTheme = 'blue',
  compact = false,
  emptyMessage = 'Aucune donnée disponible',
  showPagination = true,
  showTopXSelector = true,
  topXOptions = [5, 10, 15, 20],
  fixedPageSize = null,
  headerControls = null,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [currentTopX, setCurrentTopX] = useState(topX);

  const themeColors = {
    blue: {
      border: 'border-blue-200',
      icon: 'text-blue-500',
      badge: ['bg-blue-100 text-blue-700', 'bg-blue-50 text-blue-600', 'bg-blue-50 text-blue-500'],
    },
    green: {
      border: 'border-green-200',
      icon: 'text-green-500',
      badge: ['bg-green-100 text-green-700', 'bg-green-50 text-green-600', 'bg-green-50 text-green-500'],
    },
    yellow: {
      border: 'border-yellow-200',
      icon: 'text-yellow-500',
      badge: ['bg-yellow-100 text-yellow-700', 'bg-yellow-50 text-yellow-600', 'bg-yellow-50 text-yellow-500'],
    },
    red: {
      border: 'border-red-200',
      icon: 'text-red-500',
      badge: ['bg-red-100 text-red-700', 'bg-red-50 text-red-600', 'bg-red-50 text-red-500'],
    },
    purple: {
      border: 'border-purple-200',
      icon: 'text-purple-500',
      badge: ['bg-purple-100 text-purple-700', 'bg-purple-50 text-purple-600', 'bg-purple-50 text-purple-500'],
    },
    orange: {
      border: 'border-orange-200',
      icon: 'text-orange-500',
      badge: ['bg-orange-100 text-orange-700', 'bg-orange-50 text-orange-600', 'bg-orange-50 text-orange-500'],
    },
    pink: {
      border: 'border-pink-200',
      icon: 'text-pink-500',
      badge: ['bg-pink-100 text-pink-700', 'bg-pink-50 text-pink-600', 'bg-pink-50 text-pink-500'],
    },
  };

  const theme = themeColors[colorTheme] || themeColors.blue;

  const sortedData = useMemo(() => {
    if (!data || data.length === 0) return [];
    return [...data].sort((a, b) => (b.value || 0) - (a.value || 0));
  }, [data]);

  const shouldLimitByTopX = Boolean(fixedPageSize && showTopXSelector);
  const displayData = shouldLimitByTopX ? sortedData.slice(0, currentTopX) : sortedData;
  const pageSize = fixedPageSize || currentTopX;
  const totalPages = Math.ceil(displayData.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = displayData.slice(startIndex, endIndex);
  const displayStart = displayData.length > 0 ? startIndex + 1 : 0;
  const displayEnd = Math.min(endIndex, displayData.length);

  const handleTopXChange = (e) => {
    const newX = parseInt(e.target.value, 10);
    setCurrentTopX(newX);
    setCurrentPage(1);
  };

  const handlePrevious = () => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  };

  const handleNext = () => {
    setCurrentPage((prev) => Math.min(totalPages, prev + 1));
  };

  const handleClick = (item) => {
    if (onItemClick) onItemClick(item);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [currentTopX, data.length]);

  if (compact) {
    return (
      <div className={`w-full min-w-0 overflow-hidden bg-white rounded-lg border ${theme.border} p-2 sm:p-3 shadow-sm`}>
        <div className="flex flex-wrap items-center justify-between mb-2 pb-2 border-b border-gray-100 gap-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {title ? (
              <>
                <div className={`text-sm ${theme.icon}`}>
                  <FaRegListAlt />
                </div>
                <h4 className="font-medium text-gray-800 text-xs truncate max-w-[120px]">{title}</h4>
              </>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-1.5 min-w-0 max-w-full ml-auto">
            {showTopXSelector && (
              <select
                value={currentTopX}
                onChange={handleTopXChange}
                className="w-20 max-w-full text-xs border border-gray-300 rounded px-2 py-1 pr-6 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 truncate"
                onClick={(e) => e.stopPropagation()}
              >
                {topXOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    Top {opt}
                  </option>
                ))}
              </select>
            )}
            {headerControls}
          </div>
        </div>

        {showPagination && displayData.length > 0 && (
          <div className="flex flex-wrap items-center justify-between mb-2 pb-2 border-b border-gray-100 gap-2">
            <span className="text-[11px] text-gray-500 text-left">
              Affichage {displayStart}-{displayEnd} / {displayData.length}
            </span>
            <div className="inline-flex items-center gap-1.5 ml-auto">
              <button
                onClick={handlePrevious}
                disabled={currentPage === 1}
                className="w-7 h-7 inline-flex items-center justify-center rounded border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FaChevronLeft className="w-3 h-3 text-gray-600" />
              </button>
              <span className="text-xs text-gray-700 text-center">Page {currentPage}/{totalPages}</span>
              <button
                onClick={handleNext}
                disabled={currentPage === totalPages}
                className="w-7 h-7 inline-flex items-center justify-center rounded border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FaChevronRight className="w-3 h-3 text-gray-600" />
              </button>
            </div>
          </div>
        )}

        <div className="space-y-0.5 max-h-64 overflow-y-auto">
          {paginatedData.length > 0 ? (
            paginatedData.map((item, index) => {
              const globalIndex = startIndex + index;
              return (
                <div
                  key={item.id || globalIndex}
                  className="flex justify-between items-center py-0.5 hover:bg-gray-50 rounded px-1 cursor-pointer"
                  onClick={() => handleClick(item)}
                >
                  <div className="flex items-center gap-1.5 flex-1 min-w-0">
                    <span
                      className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-[9px] font-medium flex-shrink-0 ${
                        globalIndex === 0
                          ? 'bg-yellow-100 text-yellow-700'
                          : globalIndex === 1
                          ? 'bg-gray-100 text-gray-700'
                          : globalIndex === 2
                          ? 'bg-orange-100 text-orange-700'
                          : 'bg-gray-50 text-gray-500'
                      }`}
                    >
                      {globalIndex + 1}
                    </span>
                    <span className="font-medium text-gray-700 text-[10px] truncate flex-1" title={item.name}>
                      {item.name}
                    </span>
                  </div>
                  <span className="text-gray-600 text-[10px] font-medium flex-shrink-0 ml-2 whitespace-nowrap">
                    {item.value}
                    {item.unit && <span className="ml-0.5 text-gray-500">{item.unit}</span>}
                  </span>
                </div>
              );
            })
          ) : (
            <p className="text-gray-500 text-xs text-center py-2">{emptyMessage}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg border ${theme.border} p-4 shadow-sm`}>
      <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-100">
        <div className="flex items-center gap-3">
          {title ? (
            <>
              <div className={`text-lg ${theme.icon}`}>
                <FaRegListAlt />
              </div>
              <h4 className="font-semibold text-gray-800">{title}</h4>
            </>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          {showTopXSelector && (
            <select
              value={currentTopX}
              onChange={handleTopXChange}
              className="w-20 sm:w-24 text-xs border border-gray-300 rounded px-2 py-1 pr-6 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 truncate"
            >
              {topXOptions.map((opt) => (
                <option key={opt} value={opt}>
                  Top {opt}
                </option>
              ))}
            </select>
          )}
          {headerControls}
          <span className={`px-2.5 py-1 ${theme.badge[0]} rounded-full text-xs font-medium`}>
            {sortedData.length} éléments
          </span>
        </div>
      </div>

      <div className="space-y-2">
        {paginatedData.length > 0 ? (
          paginatedData.map((item, index) => {
            const globalIndex = startIndex + index;
            return (
              <div
                key={item.id || globalIndex}
                className="flex justify-between items-center p-2.5 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => handleClick(item)}
              >
                <div className="flex items-center gap-3">
                  <span
                    className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold flex-shrink-0 ${
                      globalIndex === 0
                        ? 'bg-yellow-100 text-yellow-700'
                        : globalIndex === 1
                        ? 'bg-gray-100 text-gray-700'
                        : globalIndex === 2
                        ? 'bg-orange-100 text-orange-700'
                        : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {globalIndex + 1}
                  </span>
                  <div>
                    <p className="font-medium text-gray-800 text-sm">{item.name}</p>
                    {item.description && <p className="text-xs text-gray-500">{item.description}</p>}
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-gray-900">
                    {item.value}
                    <span className="text-sm font-normal text-gray-500 ml-1">{item.unit}</span>
                  </p>
                  {item.trend && (
                    <p className={`text-xs ${item.trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {item.trend > 0 ? '↑' : '↓'} {Math.abs(item.trend)}%
                    </p>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center py-8">
            <FaRegListAlt className="w-12 h-12 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500 text-sm">{emptyMessage}</p>
          </div>
        )}
      </div>

      {showPagination && totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t border-gray-100">
          <button
            onClick={handlePrevious}
            disabled={currentPage === 1}
            className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
          >
            <FaChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm text-gray-600">Page {currentPage} sur {totalPages}</span>
          <button
            onClick={handleNext}
            disabled={currentPage === totalPages}
            className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
          >
            <FaChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default TopRankingCard;

